import React, { useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, Mail, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { LwModal } from '@/components/lw';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RecruitmentInvitationService } from '@/services/recruitmentInvitationService';
import type { RecruitmentCandidate } from './types';
import type { RecruitEmailStage, RecruitOnboardingEmailType } from './emailStage';

interface OnboardingEmailConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidate: RecruitmentCandidate;
  stage: RecruitEmailStage;
  onSendSuccess: () => void | Promise<void>;
}

const EMAIL_LABEL: Record<RecruitOnboardingEmailType, string> = {
  partial_onboarding: 'Partial Onboarding Credentials',
  full_onboarding: 'Full Onboarding Welcome',
  nmls_clearance: 'NMLS Clearance'
};

function credentialLabels(stage: RecruitEmailStage): string[] {
  return [
    stage.includesGmailTempPassword
      ? 'LendWise Gmail account and temporary password'
      : 'LendWise Gmail account (existing access — no temporary password)',
    'Optimal Blue username',
    stage.includesEncompassTempPassword
      ? 'Encompass user ID and temporary password'
      : 'Encompass user ID (existing access — no temporary password)'
  ];
}

export const OnboardingEmailConfirmationModal: React.FC<OnboardingEmailConfirmationModalProps> = ({
  open,
  onOpenChange,
  candidate,
  stage,
  onSendSuccess
}) => {
  const [isSending, setIsSending] = useState(false);
  const emailType = stage.emailType;

  if (!open || !emailType) {
    return null;
  }

  const label = EMAIL_LABEL[emailType];
  const isCredentialEmail = emailType !== 'nmls_clearance';
  const actionLabel = stage.action === 'retry'
    ? `Retry ${label}`
    : stage.action === 'resend'
      ? `Resend ${label}`
      : `Send ${label}`;

  const handleSend = async () => {
    setIsSending(true);
    try {
      const result = stage.action === 'retry' && stage.deliveryId
        ? await RecruitmentInvitationService.sendOnboardingEmail({
            deliveryId: stage.deliveryId
          })
        : await RecruitmentInvitationService.sendOnboardingEmail({
            recruitId: candidate.id,
            emailType,
            resend: stage.action === 'resend'
          });

      if (!result.success) {
        throw new Error(result.error || 'Email delivery failed');
      }

      const successTitle = emailType === 'partial_onboarding'
        ? 'Partial onboarding credentials sent'
        : emailType === 'nmls_clearance'
          ? 'NMLS clearance email sent'
          : 'Full onboarding email sent';
      toast.success(successTitle, {
        description: `Delivered to ${stage.recipientEmail}`
      });
      await onSendSuccess();
      onOpenChange(false);
    } catch (error) {
      toast.error(`Failed to ${stage.action === 'retry' ? 'retry' : 'send'} email`, {
        description: error instanceof Error ? error.message : 'Please try again'
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <LwModal
      size="wide"
      eyebrow="Recruiting"
      title={actionLabel}
      sub={`Review the server-owned delivery for ${candidate.firstName} ${candidate.lastName}.`}
      onClose={() => onOpenChange(false)}
      footer={
        <>
          <button
            className="btn-sm"
            onClick={() => onOpenChange(false)}
            disabled={isSending}
            type="button"
          >
            Cancel
          </button>
          <button
            className="btn-sm solid"
            onClick={handleSend}
            disabled={isSending || !stage.recipientEmail}
            type="button"
          >
            {isSending ? <Loader2 className="animate-spin" /> : <Mail />}
            {isSending ? 'Sending…' : actionLabel}
          </button>
        </>
      }
    >
      <div className="space-y-4" data-testid="onboarding-email-confirmation">
        <div className="grid gap-3 rounded-lg border bg-muted/30 p-4 sm:grid-cols-2">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Email type</p>
            <p className="mt-1 text-sm font-semibold">{label}</p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Expected recipient</p>
            <p className="mt-1 break-all text-sm font-semibold">{stage.recipientEmail || 'Unavailable'}</p>
            <p className="mt-1 text-xs text-muted-foreground">Verified again by the server when sent</p>
          </div>
        </div>

        {emailType === 'partial_onboarding' && (
          <Alert className="border-warning/40 bg-warning/10">
            <AlertCircle className="h-4 w-4 text-warning" aria-hidden="true" />
            <AlertDescription>
              This fixed compliance email states that the recruit is not authorized to conduct business in LendWise Mortgage&apos;s name or take an application in their name until NMLS authorization is recorded.
            </AlertDescription>
          </Alert>
        )}

        {emailType === 'nmls_clearance' && (
          <Alert className="border-success/40 bg-success/10">
            <ShieldCheck className="h-4 w-4 text-success" aria-hidden="true" />
            <AlertDescription>
              This short confirmation contains no credentials. It confirms authorization and that Marketing tools are unlocked.
            </AlertDescription>
          </Alert>
        )}

        <div className="rounded-lg border p-4">
          <h3 className="text-sm font-semibold">
            {isCredentialEmail ? 'Credential sections included' : 'Email contents'}
          </h3>
          {isCredentialEmail ? (
            <ul className="mt-3 space-y-2">
              {credentialLabels(stage).map((credentialLabel) => (
                <li key={credentialLabel} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden="true" />
                  {credentialLabel}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">
              Authorization confirmation, permission to take applications in their name, and Marketing unlock notice.
            </p>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            Values, recipient routing, copy, and CC are resolved by the server and cannot be edited here.
          </p>
        </div>

        {stage.delivery && (
          <p className="text-xs text-muted-foreground">
            Delivery status: {stage.delivery.status}. Attempts: {stage.delivery.attempt_count}.
          </p>
        )}
      </div>
    </LwModal>
  );
};
